import { useLayoutEffect, useRef, type RefObject } from 'react';
import { focusConversationInList } from '../features/conversations';

/**
 * Mesmo ponto de quebra de `InboxLayout.module.css` (um teste confere que os dois continuam
 * iguais): abaixo dele aparece uma coluna por vez e a outra recebe `display: none`.
 */
export const NARROW_LAYOUT_QUERY = '(max-width: 760px)';

/** Sem `matchMedia` (jsdom) considera o layout largo, onde o foco não é gerenciado. */
export function isNarrowLayout(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(NARROW_LAYOUT_QUERY).matches;
}

/**
 * Em layout estreito a lista (ou o chat) vira `display: none`; o elemento focado some e o foco
 * cai no <body>, perdendo o lugar de quem usa teclado ou leitor de tela.
 *
 * - Ao abrir uma conversa: o foco vai para o <main>.
 * - Ao voltar para a lista: o foco volta ao link da conversa que estava aberta (sem rolar a
 *   lista de forma brusca).
 *
 * Só reage a TRANSIÇÕES (abrir e fechar), nunca à primeira renderização: carregar a página direto
 * em `/c/:id` não rouba o foco. No layout largo não faz nada: lá a lista segue visível e o foco
 * fica onde a pessoa o deixou.
 */
export function useNarrowFocus(
  conversationId: string | null,
  mainRef: RefObject<HTMLElement | null>,
  listRef: RefObject<HTMLElement | null>,
): void {
  // `undefined` = ainda não houve renderização; depois guarda a conversa aberta no passo anterior.
  const previousId = useRef<string | null | undefined>(undefined);

  // Antes da pintura, para o foco não ficar um quadro no <body> (leitor de tela anunciaria isso).
  useLayoutEffect(() => {
    const previous = previousId.current;
    previousId.current = conversationId;
    if (previous === undefined || !isNarrowLayout()) return;

    if (previous === null && conversationId !== null) {
      mainRef.current?.focus();
    } else if (previous !== null && conversationId === null && listRef.current) {
      focusConversationInList(listRef.current, previous);
    }
  }, [conversationId, mainRef, listRef]);
}
